import { memo, useState } from 'react';

/**
 * Extract module name from a path (last segment)
 */
function getModuleName(path) {
  if (!path) return 'unknown';
  const segments = path.split('/').filter(Boolean);
  return segments[segments.length - 1] || path;
}

/**
 * Sidebar showing details of a selected node (directory/module)
 */
function NodeDetailsSidebar({ node, edges, onClose, onExplore, onImpactAnalysis }) {
  if (!node) return null;

  const { data } = node;
  const boundary = data.boundary;
  const coupling = data.coupling;
  const metrics = data.metrics;

  // Find connected edges
  const incomingEdges = edges?.filter(e => e.target === node.id) || [];
  const outgoingEdges = edges?.filter(e => e.source === node.id) || [];

  return (
    <div className="details-sidebar">
      <div className="sidebar-header">
        <h3>Module Details</h3>
        <button className="close-btn" onClick={onClose} title="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <div className="sidebar-body">
        {/* Module Name */}
        <div className="module-title">
          <span className="module-name">{data.label}</span>
          {boundary?.label && (
            <span
              className="layer-tag"
              style={{ backgroundColor: boundary.color }}
            >
              {boundary.label}
            </span>
          )}
        </div>
        {data.path && (
          <div className="module-path">{data.path}</div>
        )}

        {/* Coupling Metrics */}
        {coupling && (
          <div className="info-section">
            <h4 className="section-label">Coupling Metrics</h4>
            <div className="info-row">
              <span className="info-key">Fan-in:</span>
              <span className="info-val">{coupling.fanIn} dependents</span>
            </div>
            <div className="info-row">
              <span className="info-key">Fan-out:</span>
              <span className="info-val">{coupling.fanOut} dependencies</span>
            </div>
            <div className="info-row">
              <span className="info-key">Level:</span>
              <span className={`info-val coupling-level-${coupling.level}`}>
                {coupling.level}
              </span>
            </div>
            {coupling.inCycle && (
              <div className="info-row cycle">
                <span className="info-key warning">Cycle:</span>
                <span className="info-val">Part of dependency cycle</span>
              </div>
            )}
          </div>
        )}

        {/* Code Metrics */}
        {metrics && (metrics.loc > 0 || metrics.avgComplexity > 0 || metrics.churn > 0) && (
          <div className="info-section">
            <h4 className="section-label">Code Metrics</h4>
            {metrics.loc > 0 && (
              <div className="info-row">
                <span className="info-key">Lines of Code:</span>
                <span className="info-val">{metrics.loc.toLocaleString()}</span>
              </div>
            )}
            {metrics.avgComplexity > 0 && (
              <div className="info-row">
                <span className="info-key">Avg Complexity:</span>
                <span className="info-val">{metrics.avgComplexity.toFixed(1)}</span>
              </div>
            )}
            {metrics.maxComplexity > 0 && (
              <div className="info-row">
                <span className="info-key">Max Complexity:</span>
                <span className="info-val">{metrics.maxComplexity}</span>
              </div>
            )}
            {metrics.churn > 0 && (
              <div className="info-row">
                <span className="info-key">Commits (30d):</span>
                <span className="info-val">{metrics.churn}</span>
              </div>
            )}
          </div>
        )}

        {/* File/Symbol counts */}
        {(data.fileCount > 0 || data.symbolCount > 0) && (
          <div className="info-section">
            <h4 className="section-label">Contents</h4>
            {data.fileCount > 0 && (
              <div className="info-row">
                <span className="info-key">Files:</span>
                <span className="info-val">{data.fileCount}</span>
              </div>
            )}
            {data.symbolCount > 0 && (
              <div className="info-row">
                <span className="info-key">Symbols:</span>
                <span className="info-val">{data.symbolCount}</span>
              </div>
            )}
          </div>
        )}

        {/* Connections */}
        {(incomingEdges.length > 0 || outgoingEdges.length > 0) && (
          <div className="info-section">
            <h4 className="section-label">Connections</h4>
            {incomingEdges.length > 0 && (
              <div className="connection-group">
                <span className="connection-title">Depended on by ({incomingEdges.length})</span>
                <div className="connection-list">
                  {incomingEdges.slice(0, 5).map(e => (
                    <div key={e.id} className="connection-item" title={e.source}>
                      {getModuleName(e.source)}
                    </div>
                  ))}
                  {incomingEdges.length > 5 && (
                    <div className="connection-more">+{incomingEdges.length - 5} more</div>
                  )}
                </div>
              </div>
            )}
            {outgoingEdges.length > 0 && (
              <div className="connection-group">
                <span className="connection-title">Depends on ({outgoingEdges.length})</span>
                <div className="connection-list">
                  {outgoingEdges.slice(0, 5).map(e => (
                    <div key={e.id} className="connection-item" title={e.target}>
                      {getModuleName(e.target)}
                    </div>
                  ))}
                  {outgoingEdges.length > 5 && (
                    <div className="connection-more">+{outgoingEdges.length - 5} more</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="sidebar-actions">
          <button
            className="action-btn primary"
            onClick={() => onExplore?.(node)}
          >
            Explore Module
          </button>
          <button
            className="action-btn secondary"
            onClick={() => onImpactAnalysis?.(data.path || node.id)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            Impact Analysis
          </button>
        </div>
      </div>
    </div>
  );
}

export default memo(NodeDetailsSidebar);
